/**
 * <offer-group> — threshold-based offer unlock cards.
 *
 * Evaluates the unlock state in three places:
 *   1. DOMContentLoaded (or immediately if DOM is already ready) — initial load
 *   2. titan:price:update events — bundle / variant / quantity changes
 *   3. shopify:section:load — theme editor re-render
 *
 * On initial check, actively pulls the live bundle subtotal by calling
 * publishSubtotal() on a sibling bundle-group-component. This avoids a race
 * where the bundle's first broadcast happens before this element's listener
 * is attached. Fallbacks: bundle root's data-subtotal-cents, then this
 * element's own data-initial-price.
 *
 * Event contract:
 *   document.dispatchEvent(new CustomEvent('titan:price:update', {
 *     detail: { sectionId: '<section.id>', totalPrice: <cents> }
 *   }));
 *
 * Section-scoped: events with detail.sectionId that doesn't match this
 * instance's data-section-id are ignored.
 */
class OfferGroup extends HTMLElement {
  constructor() {
    super();
    this._onPriceUpdate = this._onPriceUpdate.bind(this);
    this._onSectionLoad = this._onSectionLoad.bind(this);
    this._onDomReady = this._onDomReady.bind(this);
    this._isInitializing = false;
  }

  connectedCallback() {
    this._sectionId = this.dataset.sectionId || '';
    this._items = Array.from(this.querySelectorAll('[data-offer-item]'));
    this._reduceMotion =
      typeof window.matchMedia === 'function' &&
      window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    // Idempotent bind — server-rendered markup never carries this flag,
    // so a fresh render (initial load, section reload) always rebinds.
    if (this.dataset.offerInit !== 'true') {
      document.addEventListener('titan:price:update', this._onPriceUpdate);
      document.addEventListener('shopify:section:load', this._onSectionLoad);
      this.dataset.offerInit = 'true';
    }

    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', this._onDomReady, { once: true });
    } else {
      // Microtask gives sibling custom elements defined later in the same
      // defer batch a chance to upgrade before we query them.
      queueMicrotask(() => this._runInitialCheck());
    }
  }

  disconnectedCallback() {
    document.removeEventListener('titan:price:update', this._onPriceUpdate);
    document.removeEventListener('shopify:section:load', this._onSectionLoad);
    document.removeEventListener('DOMContentLoaded', this._onDomReady);
    delete this.dataset.offerInit;
  }

  /**
   * Public API — let other code drive the group directly.
   * @param {number} cents
   */
  setPrice(cents) {
    const n = Number(cents);
    if (!Number.isFinite(n)) return;
    this._paint(n, { animate: true });
  }

  _onDomReady() {
    this._runInitialCheck();
  }

  _runInitialCheck() {
    this._isInitializing = true;

    const bundle = this._findBundleGroupInSection();
    if (bundle && typeof bundle.publishSubtotal === 'function') {
      // Dispatch is synchronous, so _onPriceUpdate fires before this returns.
      bundle.publishSubtotal();
    } else {
      const fallback = this._readFallbackCents(bundle);
      this._paint(fallback, { animate: false });
    }

    this._isInitializing = false;
  }

  _findBundleGroupInSection() {
    if (!this._sectionId) {
      return document.querySelector('bundle-group-component');
    }
    return document.querySelector(
      `bundle-group-component[data-section-id="${this._sectionId}"]`
    );
  }

  _readFallbackCents(bundleRoot) {
    if (bundleRoot && bundleRoot.dataset && bundleRoot.dataset.subtotalCents) {
      const v = Number(bundleRoot.dataset.subtotalCents);
      if (Number.isFinite(v)) return v;
    }
    const initial = Number(this.dataset.initialPrice);
    return Number.isFinite(initial) ? initial : 0;
  }

  _onPriceUpdate(event) {
    const detail = event && event.detail;
    if (!detail) return;
    if (detail.sectionId && this._sectionId && detail.sectionId !== this._sectionId) return;

    const cents = Number(detail.totalPrice);
    if (!Number.isFinite(cents)) return;

    this._paint(cents, { animate: !this._isInitializing });
  }

  _onSectionLoad(event) {
    if (!event || !event.target || typeof event.target.contains !== 'function') return;
    if (!event.target.contains(this)) return;
    this._items = Array.from(this.querySelectorAll('[data-offer-item]'));
    this._runInitialCheck();
  }

  _paint(cents, { animate }) {
    for (const item of this._items) {
      const threshold = Number(item.dataset.threshold) || 0;
      const shouldUnlock = cents >= threshold;
      const wasUnlocked = item.classList.contains('is-unlocked');

      if (shouldUnlock && !wasUnlocked) {
        item.classList.remove('is-locked');
        item.classList.add('is-unlocked');
        item.setAttribute('aria-disabled', 'false');

        if (animate && !this._reduceMotion) {
          item.classList.remove('just-unlocked');
          // Force reflow so the animation restarts on every transition.
          void item.offsetWidth;
          item.classList.add('just-unlocked');
          const onEnd = () => {
            item.classList.remove('just-unlocked');
            item.removeEventListener('animationend', onEnd);
          };
          item.addEventListener('animationend', onEnd);
        }
      } else if (!shouldUnlock && wasUnlocked) {
        item.classList.remove('is-unlocked', 'just-unlocked');
        item.classList.add('is-locked');
        item.setAttribute('aria-disabled', 'true');
      } else if (!shouldUnlock && !wasUnlocked) {
        item.classList.add('is-locked');
        item.setAttribute('aria-disabled', 'true');
      }
    }
  }
}

if (!customElements.get('offer-group')) {
  customElements.define('offer-group', OfferGroup);
}
